import { useEffect } from 'react';
import { X } from 'lucide-react';
import { CreateSpAdGroupForm } from '@/components/campaign-create/create-adgroup/CreateSpAdGroupForm';
import { isSpAutoCampaign } from '@/utils/spAdGroupCreate';
import type { SpCampaignNormalized } from '@/types/spCampaigns';

export function CreateSpAdGroupModal({
  profileId,
  campaignId,
  campaign,
  isOpen,
  onClose,
}: {
  profileId: string;
  campaignId: string;
  campaign: SpCampaignNormalized;
  isOpen: boolean;
  onClose: () => void;
}) {
  const autoCampaign = isSpAutoCampaign(campaign);

  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-start justify-center z-50 p-4 overflow-y-auto">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="new-ad-group-title"
        className="bg-surface rounded-xl w-full max-w-5xl my-6 shadow-xl"
      >
        <div className="flex items-start justify-between gap-4 px-6 pt-5 pb-3 border-b border-border sticky top-0 bg-surface rounded-t-xl z-10">
          <div>
            <h2 id="new-ad-group-title" className="text-lg font-semibold">
              New ad group
            </h2>
            <p className="text-sm text-foreground-muted mt-0.5">
              {autoCampaign ? 'Automatic targeting' : 'Manual targeting'} for {campaign.name}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-chrome-label hover:text-foreground-muted mt-0.5"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="px-6 py-4 max-h-[min(80vh,52rem)] overflow-y-auto">
          <CreateSpAdGroupForm
            profileId={profileId}
            campaignId={campaignId}
            campaignRaw={campaign.raw}
            targeting={autoCampaign ? 'AUTO' : 'MANUAL'}
            onSuccess={onClose}
            onCancel={onClose}
          />
        </div>
      </div>
    </div>
  );
}
